import {
  Chevron,
  Disclaimer,
  Div,
  DuplicateIcon,
  EditPenIcon,
  H5,
  Icon,
  Img,
  P,
  Separator,
  TaxRatesIcon,
  TrashIconRed,
  centsToDollars,
  formatter,
  lightTheme,
  renderProviderIcon,
} from '@vgrubs/components';
import { SortableList } from '../../components/SortableList';
import { TaxRatesOverriddenPill } from '../../taxRates/TaxRatesSection';
import {
  MenuOptionPriceCustomizationV2Response,
  ModifierGroupItemV2Response,
  ModifierItemV2Response,
} from 'components/src/types/menu.types';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Collapsible } from '../../MenyStyled';
import { LinkedItemNote } from '../Items/MenuItemComponents';
import { Availability } from 'components/src/types/menu.types';

export const ProviderPriceChips = ({
  customizedPrices,
}: {
  customizedPrices?: MenuOptionPriceCustomizationV2Response[] | null;
}) => {
  if (!customizedPrices || customizedPrices.length === 0) return null;
  return (
    <Div $flex $alignCenter $gap="24px" $gapSM="4px" $columnSM $alignStartSM>
      {customizedPrices.map((cp, i) => (
        <Div $flex $alignCenter $gap="6px" key={i}>
          <Div $borderRadius="50%" $border={`1px solid ${lightTheme.gray}`}>
            <Img
              alt=""
              $width="24px"
              src={renderProviderIcon(cp.provider)}
              $verticalAlign="middle"
            />
          </Div>
          <P $m="0">{formatter.format(centsToDollars(cp.price))}</P>
        </Div>
      ))}
    </Div>
  );
};

export const ModifierBoxComponent = ({
  option,
  handleRemoveOption,
  handleEditOption,
  i,
  dragHandle,
}: {
  option: Partial<ModifierItemV2Response> & {
    name: string;
    price: number;
    availability: Availability;
  };
  handleRemoveOption: (i: number) => void;
  handleEditOption: (option: ModifierItemV2Response, index: number) => void;
  i: number;
  /** Rendered before the name, for a sortable list. */
  dragHandle?: ReactNode;
}) => {
  return (
    <Div
      $box
      $p="12px 16px"
      $flex
      $alignCenter
      $between
      // $width="calc(50% - 8px)"
    >
      <Div $flex $alignCenter $gap="16px">
        {dragHandle}
        <div>
          <H5 $left $mb="4">
            {option.name}
          </H5>
          <P $left $mb="6">
            {formatter.format(centsToDollars(option.price))}
          </P>
          <Disclaimer
            $left
            $m="0"
            $color={
              option.availability === Availability.SoldOut
                ? lightTheme.primary
                : lightTheme.green
            }
          >
            {option.availability === Availability.Available
              ? 'Available'
              : 'Sold Out'}
          </Disclaimer>
          {option.linkKey && (
            <Div $mt="4">
              <LinkedItemNote />
            </Div>
          )}
        </div>
      </Div>
      <Div $flex $alignCenter $gap="24px" $gapSM="12px">
        <ProviderPriceChips customizedPrices={option.customizedPrices} />
        <Icon
          $shadow
          $pointer
          onClick={() => handleEditOption(option as ModifierItemV2Response, i)}
        >
          <EditPenIcon />
        </Icon>
        <Icon $pointer onClick={() => handleRemoveOption(i)}>
          <TrashIconRed />
        </Icon>
      </Div>
    </Div>
  );
};

export const ModifierGroupComponent = ({
  item,
  onClickDelete,
  onClick,
  onClickDuplicate,
  isPos,
  handleEditOptionPrice,
  handleEditModifier,
  handleEditTaxRates,
  isBrand,
  allowedToEditMenuPrice,
  isSorting = false,
  onReorderOptions,
}: {
  item: ModifierGroupItemV2Response;
  onClickDelete: (id: ModifierGroupItemV2Response['id']) => void;
  onClick: (item: ModifierGroupItemV2Response) => void;
  onClickDuplicate: (item: ModifierGroupItemV2Response) => void;
  isPos: boolean;
  handleEditOptionPrice: (item: ModifierItemV2Response) => void;
  handleEditModifier: (item: ModifierItemV2Response) => void;
  /** Absent when taxes cannot be set here - partner app, or a POS location. */
  handleEditTaxRates?: (item: ModifierItemV2Response) => void;
  isBrand: boolean;
  allowedToEditMenuPrice: boolean;
  /** Sort mode of the Modifiers tab: option handles on, group actions off. */
  isSorting?: boolean;
  onReorderOptions?: (options: ModifierItemV2Response[]) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [elementHeight, setElementHeight] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (ref.current) setElementHeight(ref?.current?.clientHeight);
  }, [item, isSorting]);

  // Every option has to be in reach while sorting; ending it leaves them open.
  useEffect(() => {
    if (isSorting) setExpanded(true);
  }, [isSorting]);

  return (
    <>
      <Div
        $box
        $p="16px 24px"
        $flex
        $alignCenter
        $between
        $mb="8"
        // $background={expanded && lightTheme.primary}
      >
        <Div
          $flex
          $alignCenter
          $gap="8px"
          onClick={() => setExpanded((prev) => !prev)}
          $width="100%"
          $pointer
        >
          <P
            $bold
            $m="0"
            //   $color={expanded && lightTheme.white}
          >
            {item.name}
          </P>
        </Div>
        <Div $flex $alignCenter $between $gap="24px" $gapSM="12px">
          {!isPos && !isSorting && (
            <Icon $shadow $pointer onClick={() => onClick(item)}>
              <EditPenIcon />
            </Icon>
          )}

          {!isPos && !isSorting && (
            <Icon
              $shadow
              $pointer
              onClick={() => onClickDuplicate(item)}
              color={lightTheme.primary}
              $size={24}
              $backgroundSize={35}
            >
              <Div $flex $center $width="35px" $height="35px">
                <DuplicateIcon />
              </Div>
            </Icon>
          )}
          {!isPos && !isSorting && (
            <Icon $pointer onClick={() => onClickDelete(item.id)}>
              <TrashIconRed />
            </Icon>
          )}
          <Icon
            $pointer
            $rotate={expanded ? '360deg' : '180deg'}
            onClick={() => setExpanded((prev) => !prev)}
            // color={expanded ? lightTheme.white : lightTheme.darkGray}
          >
            <Chevron />
          </Icon>
        </Div>
      </Div>
      <Collapsible expanded={expanded} elementHeight={elementHeight}>
        <div ref={ref}>
          <SortableList
            items={item.options}
            getId={(option) => option.id}
            getLabel={(option) => option.name}
            onReorder={(options) => onReorderOptions?.(options)}
            disabled={!isSorting || item.options.length < 2}
            renderItem={(option, _, dragHandle) => (
              <ModifierComponent
                item={option}
                isPos={isPos}
                handleEditOptionPrice={handleEditOptionPrice}
                handleEditModifier={handleEditModifier}
                handleEditTaxRates={isSorting ? undefined : handleEditTaxRates}
                isBrand={isBrand}
                allowedToEditMenuPrice={allowedToEditMenuPrice}
                isSorting={isSorting}
                dragHandle={dragHandle}
              />
            )}
          />
          {item.options.length === 0 ? (
            <P $m="0" $center>
              No modifiers
            </P>
          ) : null}
        </div>
      </Collapsible>
    </>
  );
};

export const ModifierComponent = ({
  item,
  isPos,
  handleEditOptionPrice,
  handleEditModifier,
  handleEditTaxRates,
  isBrand,
  allowedToEditMenuPrice,
  isSorting = false,
  dragHandle,
}: {
  item: ModifierItemV2Response;
  isPos: boolean;
  handleEditOptionPrice: (item: ModifierItemV2Response) => void;
  handleEditModifier: (item: ModifierItemV2Response) => void;
  handleEditTaxRates?: (item: ModifierItemV2Response) => void;
  isBrand: boolean;
  allowedToEditMenuPrice: boolean;
  isSorting?: boolean;
  dragHandle?: ReactNode;
}) => {
  return (
    <>
      <Separator />
      <Div $p="8px 20px" $flex $between $alignCenter>
        <Div $flex $alignCenter $gap="16px">
          {dragHandle}
          <div>
            <P $m="0">{item.name}</P>
            <Div $flex $alignCenter $gap="8px">
              <Disclaimer
                $color={
                  item.availability === Availability.SoldOut
                    ? lightTheme.primary
                    : lightTheme.green
                }
                $m="0"
              >
                {item.availability === Availability.Available
                  ? 'Available'
                  : 'Sold Out'}
              </Disclaimer>
              <TaxRatesOverriddenPill overridden={!!item.taxRatesOverridden} />
            </Div>
          </div>
        </Div>
        <Div $flex $alignCenter $gap="24px" $gapSM="12px">
          <ProviderPriceChips customizedPrices={item.customizedPrices} />
          {/* Only once the backend sends the field - it is absent on a
          deployment without the tax rates release. */}
          {handleEditTaxRates && item.taxRates && (
            <Icon
              $shadow
              $pointer
              $background={lightTheme.white}
              $backgroundSize={35}
              onClick={() => handleEditTaxRates(item)}
            >
              <TaxRatesIcon color={lightTheme.primary} />
            </Icon>
          )}
          <P $m="0">{formatter.format(centsToDollars(item.price))}</P>
          {isPos && !isSorting && (
            <Icon
              $disabled={!allowedToEditMenuPrice}
              $shadow
              $pointer
              onClick={() => allowedToEditMenuPrice && handleEditModifier(item)}
            >
              <EditPenIcon />
            </Icon>
          )}
        </Div>
      </Div>
    </>
  );
};
